import type {RumGlobal} from '@datadog/browser-rum';
import React, {
  Profiler as ReactProfiler,
  type ProfilerOnRenderCallback,
  useCallback,
} from 'react';

import {useDatadogRum} from '../../datadog/datadogRum';

type OnRenderParams = Parameters<ProfilerOnRenderCallback>;

// This function uses DD "custom vitals" to track render durations. React
// doesn't give us start and end render events, it just gives us a retrospective
// duration. That's fine, RUM can do retrospective vitals by providng a start
// time and a duration, and it figures out what to do with that info.
function logToDatadogRum(
  datadog: RumGlobal,
  extraContext?: Record<string, unknown>,
  ...props: OnRenderParams
) {
  const [
    id,
    phase,
    actualDuration,
    baseDuration,
    startTime,
    commitTime,
    interactions,
  ] = props;
  const context = {
    ...extraContext,
    commitTime,
    interactions,
  };

  // the startTime is relative to when the page loaded (like performance.now()), so we need to add the time origin to get the absolute time for Datadog
  const absoluteStartTime = performance.timeOrigin + startTime;

  datadog.addDurationVital(id, {
    startTime: absoluteStartTime,
    duration: actualDuration,
    description: phase,
    context,
  });

  // The `baseDuration` property is React's estimate of the render time in the
  // case where all the memoization is invalidated, and it has to do the most-
  // expensive render it can. In principle, the ratio between actual duration
  // and worstcase duration should be a decent proxy for "cache hit percentage"
  datadog.addDurationVital(`${id}__worstcase`, {
    startTime: absoluteStartTime,
    duration: baseDuration,
    description: phase,
    context,
  });
}

interface ProfilerProps {
  children: React.ReactElement;
  id: string;
  onRender?: ProfilerOnRenderCallback;
  // Use this to disable the profiler if you'd like to sample the profiling
  enabled?: boolean;
  // Anything else you want to log with the profiler to datadog
  extraContext?: Record<string, unknown>;
}

/**
 * This component allows profiling the render performance of a particular
 * subtree of React components. It automatically forwards perf information to
 * datadog via the "Custom Vitals" feature.
 *
 * It also behaves as a standard React `Profiler` component, allowing you the
 * user to optionally do what they want with the performance information.
 *
 * @see <https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#measure-component-level-performance-with-custom-vitals>
 * @see <https://react.dev/reference/react/Profiler>
 */
export function Profiler({
  children,
  id,
  onRender,
  enabled,
  extraContext,
}: ProfilerProps): React.ReactElement {
  const datadogRum = useDatadogRum();

  const onRenderInternal = useCallback(
    (...args: OnRenderParams) => {
      if (onRender) {
        onRender(...args);
      }

      // Profilers bubble up, so nested profilers log to parent profilers,
      // allowing parents to multiplex. We don't want that for DD logging, so
      // only send metrics if it's _this_ profiler instance that's reporting
      // render performance.
      const [renderId] = args;
      if (renderId === id) {
        logToDatadogRum(datadogRum, extraContext, ...args);
      }
    },
    [datadogRum, id, onRender, extraContext]
  );

  // if the profiler is disabled, just return the children to avoid the extra overhead of the profiler
  if (enabled === false) {
    return children;
  }

  return (
    <ReactProfiler id={id} onRender={onRenderInternal}>
      {children}
    </ReactProfiler>
  );
}
